import { useCallback, useMemo, useRef, useState } from "react";
import { uniqueVals } from "@markit/common.utils";
import {
  MembershipPromoCode,
  MembershipPromoCodeSource,
} from "@markit/common.types";
import {
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Query,
  startAfter,
  where,
} from "../firebase";
import { getUserMembershipPromoCodesRef } from "../utils/FirebaseUtils";
import useOnUnmount from "./useOnUnmount";
import { useSelector } from "react-redux";
import { getAccountState } from "../redux/slices/accountSlice";

type useLoadMembershipPromoCodesProps = {
  windowSize: number;
  active: boolean;
};

export const useLoadMembershipPromoCodes = (
  props: useLoadMembershipPromoCodesProps
) => {
  const { windowSize, active } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;

  const [fetchedPromoCodes, setFetchedPromoCodes] = useState<
    MembershipPromoCode[]
  >([]);
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [timeoutEvent, setTimeoutEvent] = useState<
    NodeJS.Timeout | undefined
  >();

  const [lastVisible, setLastVisible] = useState<string>(
    new Date().toISOString()
  );
  const [loadedAllPromoCodes, setLoadedAllPromoCodes] = useState(false);

  useOnUnmount((_latestDeps) => {
    unsubscribeArrayRef.current.forEach(
      (unsubscribe) => unsubscribe && unsubscribe()
    );
    unsubscribeArrayRef.current = []; // Clear the array after cleanup
  }, []);

  const addPromoCodeData = useCallback((promoCodes: MembershipPromoCode[]) => {
    setFetchedPromoCodes((fetchedPromoCodes) => {
      return uniqueVals(
        promoCodes.concat(fetchedPromoCodes),
        (promoCode) => promoCode.id
      ).sort((a, b) => a.alias.localeCompare(b.alias));
    });
  }, []);

  const removePromoCodeData = useCallback((promoCodeId: string) => {
    setFetchedPromoCodes((fetchedPromoCodes) => {
      const index = fetchedPromoCodes.findIndex(
        (fetched) => fetched.id === promoCodeId
      );
      if (index !== -1) {
        fetchedPromoCodes.splice(index, 1);
      }
      return [...fetchedPromoCodes];
    });
  }, []);

  const isFinished = useMemo(() => loadedAllPromoCodes, [loadedAllPromoCodes]);

  const retrievePromoCodeDataResults = useCallback(
    async (
      promoCodesQuery: Query<MembershipPromoCode>
    ): Promise<MembershipPromoCode[]> => {
      const promoCodesSnap = await getDocs(promoCodesQuery);
      const uploadedTimes = promoCodesSnap.docs.map((doc) => doc.data().alias);
      if (uploadedTimes.length > 0) {
        setLastVisible(uploadedTimes[uploadedTimes.length - 1]);
      }
      if (uploadedTimes.length < windowSize) {
        setLoadedAllPromoCodes(true);
      }

      const promoCodes = promoCodesSnap.docs.map((doc) => doc.data());
      addPromoCodeData(promoCodes);
      setIsLoading(false);
      return promoCodes;
    },
    [addPromoCodeData, windowSize]
  );

  const createPromoCodeData = useCallback(
    (promoCode: MembershipPromoCode) => {
      addPromoCodeData([promoCode]);
    },
    [addPromoCodeData]
  );

  const fetchPromoCodes = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const promoCodesRef = getUserMembershipPromoCodesRef(accountData.uid);
      const promoCodesQuery = query(
        promoCodesRef,
        orderBy("alias", "asc"),
        where("active", "==", active),
        where("source", "==", MembershipPromoCodeSource.CREATOR),
        limit(windowSize)
      );
      const promoCodes = await retrievePromoCodeDataResults(promoCodesQuery);

      const unsubscribe = onSnapshot(promoCodesQuery, (snapshot) => {
        snapshot.docChanges().forEach((message) => {
          const promoCode = message.doc.data();
          if (
            message.type === "added" &&
            !promoCodes.some((code) => code.id === promoCode.id)
          ) {
            createPromoCodeData(promoCode);
          } else if (message.type === "modified") {
            if (promoCode.active !== active) {
              removePromoCodeData(promoCode.id);
            } else {
              createPromoCodeData(promoCode);
            }
          } else if (message.type === "removed") {
            removePromoCodeData(promoCode.id);
          }
        });
      });

      unsubscribeArrayRef.current.push(unsubscribe);
    }
  }, [
    isFinished,
    isLoading,
    accountData.uid,
    active,
    windowSize,
    retrievePromoCodeDataResults,
    createPromoCodeData,
    removePromoCodeData,
  ]);

  const fetchMorePromoCodes = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const promoCodesRef = getUserMembershipPromoCodesRef(accountData.uid);
      const promoCodesQuery = query(
        promoCodesRef,
        orderBy("alias", "asc"),
        where("active", "==", active),
        where("source", "==", MembershipPromoCodeSource.CREATOR),
        startAfter(lastVisible),
        limit(windowSize)
      );
      const promoCodes = await retrievePromoCodeDataResults(promoCodesQuery);

      const unsubscribe = onSnapshot(promoCodesQuery, (snapshot) => {
        snapshot.docChanges().forEach((message) => {
          const promoCode = message.doc.data();
          if (
            message.type === "added" &&
            !promoCodes.some((code) => code.id === promoCode.id)
          ) {
            createPromoCodeData(promoCode);
          } else if (message.type === "modified") {
            if (promoCode.active !== active) {
              removePromoCodeData(promoCode.id);
            } else {
              createPromoCodeData(promoCode);
            }
          } else if (message.type === "removed") {
            removePromoCodeData(promoCode.id);
          }
        });
      });

      unsubscribeArrayRef.current.push(unsubscribe);
    }
  }, [
    isFinished,
    isLoading,
    accountData.uid,
    active,
    lastVisible,
    windowSize,
    retrievePromoCodeDataResults,
    createPromoCodeData,
    removePromoCodeData,
  ]);

  const loadSearchResultsThrottled = useCallback(
    (searchTerm: string) => {
      if (timeoutEvent) {
        clearTimeout(timeoutEvent);
      }
      setTimeoutEvent(
        setTimeout(() => {
          if (
            searchTerm.length > 1 &&
            !fetchedPromoCodes.some((code) => code.alias.includes(searchTerm))
          ) {
            fetchMorePromoCodes();
          }
        }, 500)
      );
    },
    [fetchMorePromoCodes, fetchedPromoCodes, timeoutEvent]
  );

  return {
    isFinished,
    isLoading,
    fetchedPromoCodes,
    fetchPromoCodes,
    fetchMorePromoCodes,
    loadSearchResultsThrottled,
  };
};
